import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin,
  ShoppingBag,
  TrendingDown,
  Zap,
  Tag,
  GitCompare,
  Check,
} from "lucide-react";
import { useCompare } from "@/contexts/CompareContext";
import { StockAlertButton } from "@/components/StockAlertButton";
import { LiveMenuBadge } from "@/components/LiveMenuBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { TerpeneChipButton } from "@/components/TerpeneChipButton";
import { isTrustedImageUrl } from "@/lib/imageTrust";
import { externalLinkProps } from "@/lib/externalLink";
import { withUtm } from "@/lib/utm";
import { trackEvent } from "@/lib/analytics";

const TYPE_COLOR = {
  sativa: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  indica: "text-violet-300 bg-violet-500/10 border-violet-500/30",
  hybrid: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
};

const STOCK_LABEL = {
  in_stock: "In stock",
  low_stock: "Low stock",
  out_of_stock: "Sold out",
};

const CATEGORY_LABEL = {
  flower: "Flower",
  vape: "Vape",
  vaporizers: "Vape",
  concentrate: "Concentrate",
  concentrates: "Concentrate",
  edible: "Edible",
  edibles: "Edible",
  tincture: "Tincture",
  tinctures: "Tincture",
  topical: "Topical",
  topicals: "Topical",
  "pre-roll": "Pre-roll",
  "pre-rolls": "Pre-roll",
};

// Detect "real" product photos vs. potentially-rotated Unsplash stock —
// shared helper lives in @/lib/imageTrust so DispensaryCover etc. can reuse.

export const ProductCard = ({ product, utm }) => {
  const isSample = !!product.is_sample;
  const out = product.stock_status === "out_of_stock";
  const onDeal = !isSample && product.deal_active && product.original_price > product.price;
  const discountPct = onDeal
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;
  const { has, toggle, items, MAX } = useCompare();
  const inCompare = has(product.id);
  const cardRef = useRef(null);

  // Fire one "impression" event when the card first scrolls into view
  useEffect(() => {
    if (!cardRef.current) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired) {
            fired = true;
            trackEvent("impression", product.id);
            io.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, [product.id]);

  const handleOrderClick = () => trackEvent("order_click", product.id);
  const handleCardClick = () => trackEvent("card_click", product.id);

  return (
    <div
      ref={cardRef}
      data-testid={`product-card-${product.id}`}
      className={`card-hover bg-card border rounded-2xl overflow-hidden flex flex-col ${
        product.boosted ? "border-accent/40 ring-1 ring-accent/20" : "border-border"
      }`}
    >
      <div className="relative aspect-[5/4] bg-white overflow-hidden">
        {isTrustedImageUrl(product.image_url) ? (
          <img
            src={product.image_url}
            alt={product.strain_name}
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
            data-testid={`product-image-${product.id}`}
            onError={(e) => {
              // iOS PWAs sometimes give up on lazy-loaded images after a
              // single network blip and never retry. Attempt one cache-
              // busted reload before falling back to the placeholder so a
              // momentary CDN hiccup doesn't strand a real photo behind a
              // blank tile for the rest of the session.
              const img = e.currentTarget;
              const tries = parseInt(img.dataset.retry || "0", 10);
              if (tries < 1) {
                img.dataset.retry = String(tries + 1);
                const sep = img.src.includes("?") ? "&" : "?";
                img.src = `${product.image_url}${sep}r=${Date.now()}`;
                return;
              }
              const wrap = img.parentElement;
              if (wrap) {
                img.style.display = "none";
                const fb = wrap.querySelector("[data-fallback]");
                if (fb) fb.style.display = "block";
              }
            }}
          />
        ) : null}
        <div
          data-fallback
          data-testid={`product-placeholder-wrap-${product.id}`}
          style={{
            display: isTrustedImageUrl(product.image_url) ? "none" : "block",
            position: "absolute",
            inset: 0,
          }}
        >
          <ProductPlaceholder
            strainName={product.strain_name}
            brand={product.brand}
            category={product.category}
            testId={`product-placeholder-${product.id}`}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-card/60 via-transparent to-transparent" />

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.boosted && (
            <span
              data-testid={`boosted-badge-${product.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full border"
              style={{
                color: "#D4A268",
                borderColor: "rgba(212,162,104,0.5)",
                background: "rgba(212,162,104,0.15)",
              }}
            >
              <Zap className="w-3 h-3" strokeWidth={2.4} />
              Sponsored
            </span>
          )}
          {onDeal && (
            <span
              data-testid={`deal-badge-${product.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full border bg-rose-500/15 text-rose-300 border-rose-500/40"
            >
              <Tag className="w-3 h-3" strokeWidth={2.4} />
              {discountPct}% OFF
            </span>
          )}
          {!product.boosted && !onDeal && product.dispensary_featured && (
            <Badge className="pill-accent border text-[10px] uppercase tracking-[0.2em] font-semibold">
              Featured
            </Badge>
          )}
          {isSample && (
            <span
              data-testid={`sample-badge-${product.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full border bg-sky-500/15 text-sky-300 border-sky-500/40"
              title={product.sample_notice || "Menu preview — live inventory syncs once partner integration is live."}
            >
              Menu preview
            </span>
          )}
          {product.is_lowest_price && !onDeal && (
            <span
              data-testid={`lowest-badge-${product.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
            >
              <TrendingDown className="w-3 h-3" strokeWidth={2} />
              Lowest
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 flex gap-1.5 items-start">
          <span
            className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full border ${TYPE_COLOR[product.strain_type] || ""}`}
          >
            {product.strain_type}
          </span>
          <FavoriteButton product={product} />
        </div>

        {/* Compare toggle bottom-right */}
        <button
          data-testid={`compare-toggle-${product.id}`}
          onClick={() => toggle(product)}
          disabled={!inCompare && items.length >= MAX}
          title={inCompare ? "Remove from compare" : "Add to compare"}
          className={`absolute bottom-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors backdrop-blur-md ${
            inCompare
              ? "bg-primary text-primary-foreground"
              : "bg-card/80 text-foreground border border-border hover:bg-card"
          } disabled:opacity-40`}
        >
          {inCompare ? (
            <Check className="w-4 h-4" />
          ) : (
            <GitCompare className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span
                data-testid={`product-category-${product.id}`}
                className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded text-primary border border-primary/30 bg-primary/5"
              >
                {CATEGORY_LABEL[(product.category || "").toLowerCase()] || product.category || "—"}
              </span>
              {product.brand && (
                <span className="text-xs text-muted-foreground truncate">{product.brand}</span>
              )}
            </div>
            <Link
              to={`/strains/${product.strain_slug}`}
              data-testid={`product-card-strain-${product.id}`}
              onClick={handleCardClick}
              className="font-serif text-xl hover:underline underline-offset-4 decoration-accent block leading-snug"
            >
              {product.strain_name}
            </Link>
          </div>
          <div className="text-right">
            <div className="price-tag text-2xl">
              {product.price != null ? `$${Number(product.price).toFixed(2)}` : "—"}
            </div>
            {onDeal && product.original_price != null && (
              <div className="text-xs text-muted-foreground line-through">
                ${Number(product.original_price).toFixed(2)}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {product.unit}
            </div>
          </div>
        </div>

        <Link
          to={`/dispensaries/${product.dispensary_id}`}
          data-testid={`product-card-disp-${product.id}`}
          onClick={handleCardClick}
          className="flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground mt-1"
        >
          <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="truncate">{product.dispensary_name}</span>
          {product.dispensary_live_menu && (
            <LiveMenuBadge size="xs" className="shrink-0" />
          )}
          {product.distance_mi != null && (
            <span className="text-xs text-muted-foreground ml-auto">
              {product.distance_mi} mi
            </span>
          )}
        </Link>

        <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
          {product.thc_pct != null && Number.isFinite(Number(product.thc_pct)) && (
            <span className="pill">THC {Number(product.thc_pct).toFixed(1)}%</span>
          )}
          {product.thc_mg != null && Number.isFinite(Number(product.thc_mg)) && Number(product.thc_mg) > 0 && (
            <span className="pill">THC {Number(product.thc_mg)} mg</span>
          )}
          {product.cbd_pct != null && Number(product.cbd_pct) > 0 && (
            <span className="pill">CBD {Number(product.cbd_pct).toFixed(1)}%</span>
          )}
          {product.cbd_mg != null && Number(product.cbd_mg) > 0 && (
            <span className="pill">CBD {Number(product.cbd_mg)} mg</span>
          )}
          <span
            className={`pill ${
              isSample
                ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                : out
                ? "bg-destructive/20 text-destructive border-destructive/30"
                : product.stock_status === "low_stock"
                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                  : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
            }`}
          >
            {STOCK_LABEL[product.stock_status] || (isSample ? "Menu preview" : "—")}
          </span>
          {product.dispensary_pickup && <span className="pill">Pickup</span>}
          {product.dispensary_delivery && <span className="pill">Delivery</span>}
          {product.strain_slug && (
            <TerpeneChipButton
              strainSlug={product.strain_slug}
              strainName={product.strain_name}
              productId={product.id}
            />
          )}
        </div>

        {out && !isSample ? (
          <StockAlertButton
            product={product}
            className="mt-5 w-full h-11"
          />
        ) : (
          <Button
            asChild
            data-testid={`product-card-order-${product.id}`}
            className="mt-5 w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a
              {...externalLinkProps(withUtm(product.order_url, utm))}
              onClick={handleOrderClick}
            >
              <ShoppingBag className="w-4 h-4 mr-2" strokeWidth={1.6} />
              {(() => {
                const src = (product.source || "").toLowerCase();
                if (src.includes("sunnyside")) return "Visit on Sunnyside";
                if (src.includes("trulieve")) return "Visit on Trulieve";
                if (src.includes("curaleaf")) return "Visit on Curaleaf";
                return "Visit Store";
              })()}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};
